import React from "react";
import { Link } from "react-router-dom";
import Icon from "../components/AppIcon";
import { KnowtranLogo } from "../constants/images";

const PrivacyPolicy: React.FC = () => {
  const handleEmailPress = () => {
    window.open("mailto:development@insytix.com", "_blank");
  };

  const handleWebsitePress = () => {
    window.open("https://www.knowtran.com", "_blank");
  };

  const SectionHeader = ({ title }: { title: string }) => (
    <h2 className="font-jakarta font-700 text-2xl text-foreground mt-8 mb-4">
      {title}
    </h2>
  );

  const SubSectionHeader = ({ title }: { title: string }) => (
    <h3 className="font-jakarta font-700 text-lg text-foreground mt-6 mb-3">
      {title}
    </h3>
  );

  const BulletPoint = ({ text }: { text: string }) => (
    <div className="flex mb-3">
      <span className="text-muted-foreground text-sm mr-3 mt-1">•</span>
      <span className="text-sm text-muted-foreground flex-1 leading-6">
        {text}
      </span>
    </div>
  );

  const DefinitionItem = ({
    term,
    definition,
  }: {
    term: string;
    definition: string;
  }) => (
    <div className="mb-4">
      <span className="text-sm font-jakarta font-700 text-foreground">
        {term}
      </span>
      <span className="text-sm text-muted-foreground leading-6 mt-1 block">
        {definition}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface to-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-16 h-16 flex items-center justify-center">
                <img
                  src={KnowtranLogo}
                  alt="KnowTran"
                  className="w-full h-full object-contain cursor-pointer  "
                />
              </div>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors duration-300"
            >
              <Icon name="ArrowLeft" size={16} color="currentColor" />
              <span className="font-inter font-600">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content - Full Width, No Background */}
      <main className="w-full px-6 lg:px-8 py-12">
        <div className="w-full">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-jakarta font-700 text-4xl lg:text-5xl text-foreground mb-4">
              Privacy Policy
            </h1>
            <p className="font-inter font-500 text-muted-foreground text-lg">
              Last updated: September 02, 2025
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-6">
            <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
              This Privacy Policy describes Our policies and procedures on the
              collection, use and disclosure of Your information when You use
              the Service and tells You about Your privacy rights and how the
              law protects You.
            </p>
            <p className="font-inter font-500 text-muted-foreground leading-7">
              We use Your Personal data to provide and improve the Service. By
              using the Service, You agree to the collection and use of
              information in accordance with this Privacy Policy. This Privacy
              Policy has been created with the help of the Privacy Policy
              Generator.
            </p>
          </div>

          {/* Interpretation and Definitions */}
          <SectionHeader title="Interpretation and Definitions" />

          <SubSectionHeader title="Interpretation" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            The words of which the initial letter is capitalized have meanings
            defined under the following conditions. The following definitions
            shall have the same meaning regardless of whether they appear in
            singular or in plural.
          </p>

          <SubSectionHeader title="Definitions" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            For the purposes of this Privacy Policy:
          </p>

          <DefinitionItem
            term="Account"
            definition="means a unique account created for You to access our Service or parts of our Service."
          />
          <DefinitionItem
            term="Affiliate"
            definition={`means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.`}
          />
          <DefinitionItem
            term="Application"
            definition="refers to Knowtran, the software program provided by the Company."
          />
          <DefinitionItem
            term="Company"
            definition={`(referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Insytix, Panpozhi, Tenkasi TN - 627807.`}
          />
          <DefinitionItem
            term="Cookies"
            definition="are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses."
          />
          <DefinitionItem
            term="Country"
            definition="refers to: Tamil Nadu, India"
          />
          <DefinitionItem
            term="Device"
            definition="means any device that can access the Service such as a computer, a cellphone or a digital tablet."
          />
          <DefinitionItem
            term="Personal Data"
            definition="is any information that relates to an identified or identifiable individual."
          />
          <DefinitionItem
            term="Service"
            definition="refers to the Application or the Website or both."
          />
          <DefinitionItem
            term="Service Provider"
            definition="means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used."
          />
          <DefinitionItem
            term="Usage Data"
            definition="refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit)."
          />
          <div className="mb-4">
            <span className="text-sm font-jakarta font-700 text-foreground">
              Website
            </span>
            <span className="text-sm text-muted-foreground leading-6 mt-1 block">
              refers to Knowtran, accessible from{" "}
              <button
                onClick={handleWebsitePress}
                className="text-primary hover:text-primary/80 underline font-inter font-600"
              >
                https://www.knowtran.com
              </button>
            </span>
          </div>
          <DefinitionItem
            term="You"
            definition="means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable."
          />

          {/* Collecting and Using Your Personal Data */}
          <SectionHeader title="Collecting and Using Your Personal Data" />

          <SubSectionHeader title="Types of Data Collected" />

          <SubSectionHeader title="Personal Data" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            While using Our Service, We may ask You to provide Us with certain
            personally identifiable information that can be used to contact or
            identify You. Personally identifiable information may include, but
            is not limited to:
          </p>

          <BulletPoint text="Email address" />
          <BulletPoint text="First name and last name" />
          <BulletPoint text="Phone number" />
          <BulletPoint text="Address, State, Province, ZIP/Postal code, City" />
          <BulletPoint text="Usage Data" />

          <SubSectionHeader title="Usage Data" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Usage Data is collected automatically when using the Service.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Usage Data may include information such as Your Device's Internet
            Protocol address (e.g. IP address), browser type, browser version,
            the pages of our Service that You visit, the time and date of Your
            visit, the time spent on those pages, unique device identifiers and
            other diagnostic data.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            When You access the Service by or through a mobile device, We may
            collect certain information automatically, including, but not
            limited to, the type of mobile device You use, Your mobile device
            unique ID, the IP address of Your mobile device, Your mobile
            operating system, the type of mobile Internet browser You use,
            unique device identifiers and other diagnostic data.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7">
            We may also collect information that Your browser sends whenever You
            visit our Service or when You access the Service by or through a
            mobile device.
          </p>

          {/* Tracking Technologies and Cookies */}
          <SectionHeader title="Tracking Technologies and Cookies" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            We use Cookies and similar tracking technologies to track the
            activity on Our Service and store certain information. Tracking
            technologies used are beacons, tags, and scripts to collect and
            track information and to improve and analyze Our Service. The
            technologies We use may include:
          </p>

          <BulletPoint text="Cookies or Browser Cookies. A cookie is a small file placed on Your Device. You can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do not accept Cookies, You may not be able to use some parts of our Service. Unless you have adjusted Your browser setting so that it will refuse Cookies, our Service may use Cookies." />
          <BulletPoint text="Web Beacons. Certain sections of our Service and our emails may contain small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages or opened an email and for other related website statistics (for example, recording the popularity of a certain section and verifying system and server integrity)." />

          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies
            remain on Your personal computer or mobile device when You go
            offline, while Session Cookies are deleted as soon as You close Your
            web browser. Learn more about cookies on the Privacy Policies
            website article.
          </p>

          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            We use both Session and Persistent Cookies for the purposes set out
            below:
          </p>

          <SubSectionHeader title="Necessary / Essential Cookies" />
          <BulletPoint text="Type: Session Cookies" />
          <BulletPoint text="Administered by: Us" />
          <BulletPoint text="Purpose: These Cookies are essential to provide You with services available through the Website and to enable You to use some of its features. They help to authenticate users and prevent fraudulent use of user accounts. Without these Cookies, the services that You have asked for cannot be provided, and We only use these Cookies to provide You with those services." />

          <SubSectionHeader title="Cookies Policy / Notice Acceptance Cookies" />
          <BulletPoint text="Type: Persistent Cookies" />
          <BulletPoint text="Administered by: Us" />
          <BulletPoint text="Purpose: These Cookies identify if users have accepted the use of cookies on the Website." />

          <SubSectionHeader title="Functionality Cookies" />
          <BulletPoint text="Type: Persistent Cookies" />
          <BulletPoint text="Administered by: Us" />
          <BulletPoint text="Purpose: These Cookies allow us to remember choices You make when You use the Website, such as remembering your login details or language preference. The purpose of these Cookies is to provide You with a more personal experience and to avoid You having to re-enter your preferences every time You use the Website." />

          <p className="font-inter font-500 text-muted-foreground leading-7">
            For more information about the cookies we use and your choices
            regarding cookies, please visit our Cookies Policy or the Cookies
            section of our Privacy Policy.
          </p>

          {/* Use of Your Personal Data */}
          <SectionHeader title="Use of Your Personal Data" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            The Company may use Personal Data for the following purposes:
          </p>

          <BulletPoint text="To provide and maintain our Service, including to monitor the usage of our Service." />
          <BulletPoint text="To manage Your Account: to manage Your registration as a user of the Service. The Personal Data You provide can give You access to different functionalities of the Service that are available to You as a registered user." />
          <BulletPoint text="For the performance of a contract: the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service." />
          <BulletPoint text="To contact You: To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication, such as a mobile application's push notifications regarding updates or informative communications related to the functionalities, products or contracted services, including the security updates, when necessary or reasonable for their implementation." />
          <BulletPoint text="To provide You with news, special offers and general information about other goods, services and events which we offer that are similar to those that you have already purchased or enquired about unless You have opted not to receive such information." />
          <BulletPoint text="To manage Your requests: To attend and manage Your requests to Us." />
          <BulletPoint text="For business transfers: We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which Personal Data held by Us about our Service users is among the assets transferred." />
          <BulletPoint text="For other purposes: We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our Service, products, services, marketing and your experience." />

          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            We may share Your personal information in the following situations:
          </p>

          <BulletPoint text="With Service Providers: We may share Your personal information with Service Providers to monitor and analyze the use of our Service, to contact You." />
          <BulletPoint text="For business transfers: We may share or transfer Your personal information in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business to another company." />
          <BulletPoint text="With Affiliates: We may share Your information with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include Our parent company and any other subsidiaries, joint venture partners or other companies that We control or that are under common control with Us." />
          <BulletPoint text="With business partners: We may share Your information with Our business partners to offer You certain products, services or promotions." />
          <BulletPoint text="With other users: when You share personal information or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside." />
          <BulletPoint text="With Your consent: We may disclose Your personal information for any other purpose with Your consent." />

          {/* Retention of Your Personal Data */}
          <SectionHeader title="Retention of Your Personal Data" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            The Company will retain Your Personal Data only for as long as is
            necessary for the purposes set out in this Privacy Policy. We will
            retain and use Your Personal Data to the extent necessary to comply
            with our legal obligations (for example, if we are required to
            retain your data to comply with applicable laws), resolve disputes,
            and enforce our legal agreements and policies.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7">
            The Company will also retain Usage Data for internal analysis
            purposes. Usage Data is generally retained for a shorter period of
            time, except when this data is used to strengthen the security or to
            improve the functionality of Our Service, or We are legally
            obligated to retain this data for longer time periods.
          </p>

          {/* Transfer of Your Personal Data */}
          <SectionHeader title="Transfer of Your Personal Data" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Your information, including Personal Data, is processed at the
            Company's operating offices and in any other places where the
            parties involved in the processing are located. It means that this
            information may be transferred to — and maintained on — computers
            located outside of Your state, province, country or other
            governmental jurisdiction where the data protection laws may differ
            than those from Your jurisdiction.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Your consent to this Privacy Policy followed by Your submission of
            such information represents Your agreement to that transfer.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7">
            The Company will take all steps reasonably necessary to ensure that
            Your data is treated securely and in accordance with this Privacy
            Policy and no transfer of Your Personal Data will take place to an
            organization or a country unless there are adequate controls in
            place including the security of Your data and other personal
            information.
          </p>

          {/* Delete Your Personal Data */}
          <SectionHeader title="Delete Your Personal Data" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            You have the right to delete or request that We assist in deleting
            the Personal Data that We have collected about You.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Our Service may give You the ability to delete certain information
            about You from within the Service.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            You may update, amend, or delete Your information at any time by
            signing in to Your Account, if you have one, and visiting the
            account settings section that allows you to manage Your personal
            information. You may also contact Us to request access to, correct,
            or delete any personal information that You have provided to Us.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7">
            Please note, however, that We may need to retain certain information
            when we have a legal obligation or lawful basis to do so.
          </p>

          {/* Disclosure of Your Personal Data */}
          <SectionHeader title="Disclosure of Your Personal Data" />

          <SubSectionHeader title="Business Transactions" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            If the Company is involved in a merger, acquisition or asset sale,
            Your Personal Data may be transferred. We will provide notice before
            Your Personal Data is transferred and becomes subject to a different
            Privacy Policy.
          </p>

          <SubSectionHeader title="Law enforcement" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Under certain circumstances, the Company may be required to disclose
            Your Personal Data if required to do so by law or in response to
            valid requests by public authorities (e.g. a court or a government
            agency).
          </p>

          <SubSectionHeader title="Other legal requirements" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            The Company may disclose Your Personal Data in the good faith belief
            that such action is necessary to:
          </p>
          <BulletPoint text="Comply with a legal obligation" />
          <BulletPoint text="Protect and defend the rights or property of the Company" />
          <BulletPoint text="Prevent or investigate possible wrongdoing in connection with the Service" />
          <BulletPoint text="Protect the personal safety of Users of the Service or the public" />
          <BulletPoint text="Protect against legal liability" />

          {/* Security of Your Personal Data */}
          <SectionHeader title="Security of Your Personal Data" />
          <p className="font-inter font-500 text-muted-foreground leading-7">
            The security of Your Personal Data is important to Us, but remember
            that no method of transmission over the Internet, or method of
            electronic storage is 100% secure. While We strive to use
            commercially acceptable means to protect Your Personal Data, We
            cannot guarantee its absolute security.
          </p>

          {/* Children's Privacy */}
          <SectionHeader title="Children's Privacy" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Our Service does not address anyone under the age of 13. We do not
            knowingly collect personally identifiable information from anyone
            under the age of 13. If You are a parent or guardian and You are
            aware that Your child has provided Us with Personal Data, please
            contact Us. If We become aware that We have collected Personal Data
            from anyone under the age of 13 without verification of parental
            consent, We take steps to remove that information from Our servers.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7">
            If We need to rely on consent as a legal basis for processing Your
            information and Your country requires consent from a parent, We may
            require Your parent's consent before We collect and use that
            information.
          </p>

          {/* Links to Other Websites */}
          <SectionHeader title="Links to Other Websites" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Our Service may contain links to other websites that are not
            operated by Us. If You click on a third party link, You will be
            directed to that third party's site. We strongly advise You to
            review the Privacy Policy of every site You visit.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7">
            We have no control over and assume no responsibility for the
            content, privacy policies or practices of any third party sites or
            services.
          </p>

          {/* Changes to this Privacy Policy */}
          <SectionHeader title="Changes to this Privacy Policy" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            We may update Our Privacy Policy from time to time. We will notify
            You of any changes by posting the new Privacy Policy on this page.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            We will let You know via email and/or a prominent notice on Our
            Service, prior to the change becoming effective and update the "Last
            updated" date at the top of this Privacy Policy.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7">
            You are advised to review this Privacy Policy periodically for any
            changes. Changes to this Privacy Policy are effective when they are
            posted on this page.
          </p>

          {/* Contact Us */}
          <SectionHeader title="Contact Us" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            If you have any questions about this Privacy Policy, You can contact
            us:
          </p>
          <div className="mb-8">
            <p className="font-inter font-500 text-muted-foreground mb-3">
              By email:
            </p>
            <button
              onClick={handleEmailPress}
              className="text-primary hover:text-primary/80 underline font-inter font-600"
            >
              development@insytix.com
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
